import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * The single icon surface for the app. Feature code imports icon glyphs from the ui
 * layer (`import { Icon, Icons } from '@/ui'`) and renders `<Icon as={Icons.Check} />`,
 * so `lucide-react` stays confined to `src/ui/**` (enforced by the ESLint boundary).
 */
export type { LucideIcon };

type IconSize = 'inherit' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type IconTone =
  | 'current' | 'primary' | 'secondary' | 'muted' | 'action' | 'inverse'
  | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const sizeMap: Record<IconSize, string> = {
  inherit: '1em',
  xs: '0.875rem',
  sm: '1rem',
  md: '1.25rem',
  lg: '1.5rem',
  xl: '2rem',
};
const toneMap: Record<IconTone, string> = {
  current: 'currentColor',
  primary: 'text.primary',
  secondary: 'text.secondary',
  muted: 'text.muted',
  action: 'action.primary',
  inverse: 'text.inverse',
  brand: 'brand.fg',
  success: 'status.success',
  warning: 'status.warning',
  danger: 'status.danger',
  info: 'status.info',
};

const Wrap = chakra('span', {
  base: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
});

export interface IconProps {
  /** A lucide-react icon component. */
  as: LucideIcon;
  size?: IconSize;
  tone?: IconTone;
  strokeWidth?: number;
  /** Accessible label. When omitted the icon is decorative (aria-hidden). */
  label?: string;
  className?: string;
}

export const Icon = forwardRef<HTMLSpanElement, IconProps>(function Icon(
  { as: Glyph, size = 'inherit', tone = 'current', strokeWidth = 2, label, className },
  ref,
) {
  const dim = sizeMap[size];
  const sx: HTMLChakraProps<'span'> = { color: toneMap[tone], fontSize: dim };
  return (
    <Wrap
      ref={ref}
      className={className}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...sx}
    >
      <Glyph width="1em" height="1em" strokeWidth={strokeWidth} aria-hidden />
    </Wrap>
  );
});
